import i18next from 'i18next';
import { batch } from 'react-redux';
import { getSoundFileSrc } from '../base/media/functions';
import { getLocalParticipant, getParticipantById, getParticipantCount, getParticipantCountWithFake, getParticipantDisplayName, getRemoteParticipants, isLocalParticipantModerator, isParticipantModerator, isScreenShareParticipant } from '../base/participants/functions';
import { PARTICIPANT_ROLE } from '../base/participants/constants';
import { getConferenceState, getCurrentConference } from '../base/conference/functions';
import { toState } from '../base/redux/functions';
import { registerSound, unregisterSound } from '../base/sounds/actions';
import { openSettingsDialog } from '../settings/actions.web';
import { SETTINGS_TABS } from '../settings/constants';
import { hideNotification, showErrorNotification, showNotification, showWarningNotification } from '../notifications/actions';
import { NOTIFICATION_ICON, NOTIFICATION_TIMEOUT_TYPE } from '../notifications/constants';
import { BUTTON_TYPES } from '../base/ui/constants.any';
import { setE2EERoomOwner, setMediaEncryptionKey } from './actions';
import { E2EE_OFF_SOUND_ID, E2EE_ON_SOUND_ID, AUTHKEY_MESSAGE_TYPE, AUTHKEY_ROOM_OWNER_COMMAND, MAX_MODE_LIMIT, MAX_MODE_THRESHOLD } from './constants';
import AuthKeyService from './authkey/authKeyService';
import logger from './logger';
import { E2EE_OFF_SOUND_FILE, E2EE_ON_SOUND_FILE } from './sounds';
let authKeyProfileSettingsPromptedConference;
function getAppConference() {
    return typeof APP !== 'undefined' ? APP.conference : undefined;
}
/**
 * Returns the AuthKey service instance, creating it on APP.conference if needed.
 * Never throws — returns null when APP.conference is unavailable.
 *
 * @returns {AuthKeyService|null}
 */
function getAuthKeyAuthenticator() {
    const appConference = getAppConference();
    if (!appConference) {
        return null;
    }
    if (!appConference.authKeyAuthenticator) {
        appConference.authKeyAuthenticator = new AuthKeyService();
        appConference.authKeyAuthenticator.initAuthKeyService()
            .catch((error) => logger.error('[authkey] Failed to init service', error));
    }
    return appConference.authKeyAuthenticator;
}
function ensureAuthKeyService() {
    getAuthKeyAuthenticator();
}
/**
 * Waits for AuthKey service init to finish and AKE helpers to be registered.
 *
 * @returns {Promise<AuthKeyService|null>}
 */
export async function ensureAuthKeyServiceReady() {
    const authenticator = getAuthKeyAuthenticator();
    if (!authenticator) {
        return null;
    }
    try {
        await authenticator.initAuthKeyService();
    }
    catch (error) {
        logger.error('[authkey] ensureAuthKeyServiceReady: service init failed', error);
        return null;
    }
    if (typeof authenticator.serviceWASM?.AkeStart !== 'function') {
        logger.error('[authkey] ensureAuthKeyServiceReady: AkeStart unavailable after init');
        return null;
    }
    return authenticator;
}
function showAuthKeyServiceNotReadyNotification(dispatch) {
    dispatch(showErrorNotification({
        titleKey: 'notify.e2ee',
        descriptionKey: 'notify.e2eeAuthKeyNotLoaded',
        icon: NOTIFICATION_ICON.ERROR
    }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
}
export { ensureAuthKeyService, getAuthKeyAuthenticator };
export function doesEveryoneSupportE2EE(stateful) {
    const state = toState(stateful);
    const { numberOfParticipantsNotSupportingE2EE } = state['features/base/participants'];
    const { e2eeSupported } = state['features/base/conference'];
    const participantCount = getParticipantCountWithFake(state);
    if (participantCount === 1) {
        return e2eeSupported;
    }
    return numberOfParticipantsNotSupportingE2EE === 0;
}
export function isMaxModeReached(stateful) {
    const participantCount = getParticipantCount(toState(stateful));
    return participantCount >= MAX_MODE_LIMIT;
}
export function isMaxModeThresholdReached(stateful) {
    const participantCount = getParticipantCount(toState(stateful));
    return participantCount >= MAX_MODE_LIMIT + MAX_MODE_THRESHOLD;
}
export function displayVerification(state, pId) {
    const { conference } = state['features/base/conference'];
    const participant = getParticipantById(state, pId);
    return Boolean(conference?.isE2EEEnabled?.() && !shouldDisplayModeratorBadge(state, participant));
}
export function unregisterE2eeAudioFiles(dispatch) {
    dispatch(unregisterSound(E2EE_OFF_SOUND_ID));
    dispatch(unregisterSound(E2EE_ON_SOUND_ID));
}
export function registerE2eeAudioFiles(dispatch, shouldUnregister) {
    const language = i18next.language;
    shouldUnregister && unregisterE2eeAudioFiles(dispatch);
    dispatch(registerSound(E2EE_OFF_SOUND_ID, getSoundFileSrc(E2EE_OFF_SOUND_FILE, language)));
    dispatch(registerSound(E2EE_ON_SOUND_ID, getSoundFileSrc(E2EE_ON_SOUND_FILE, language)));
}
export function isLocalModerator() {
    if (typeof APP === 'undefined' || !APP.store) {
        return false;
    }
    return isAuthKeyE2EEModerator(APP.store.getState());
}
/**
 * Whether AuthKey externally manages the media encryption key.
 *
 * @param {IStateful} stateful - Redux state.
 * @returns {boolean}
 */
export function isExternallyManagedAuthKey(stateful) {
    return Boolean(toState(stateful)['features/base/config'].e2ee?.externallyManagedKey);
}
/**
 * Maps Prosody/XMPP role to the role stored in Redux for the local participant.
 * With muc_allowners every occupant receives XMPP moderator; only the AuthKey
 * room owner should be treated as moderator in the UI.
 *
 * @param {IStateful} stateful - Redux state.
 * @param {string} xmppRole - Role reported by lib-jitsi-meet.
 * @returns {string}
 */
export function getEffectiveLocalParticipantRole(stateful, xmppRole) {
    if (!isExternallyManagedAuthKey(stateful) || xmppRole !== PARTICIPANT_ROLE.MODERATOR) {
        return xmppRole;
    }
    const state = toState(stateful);
    const localId = getLocalParticipant(state)?.id;
    const ownerId = getE2EERoomOwnerId(stateful);
    if (ownerId) {
        return localId === ownerId ? PARTICIPANT_ROLE.MODERATOR : PARTICIPANT_ROLE.PARTICIPANT;
    }
    return getParticipantCount(state) <= 1 ? PARTICIPANT_ROLE.MODERATOR : PARTICIPANT_ROLE.PARTICIPANT;
}
/**
 * Participant id allowed to enable AuthKey E2EE for the current conference.
 *
 * @param {IStateful} stateful - Redux state.
 * @returns {string|undefined}
 */
export function getE2EERoomOwnerId(stateful) {
    return toState(stateful)['features/e2ee'].e2eeRoomOwnerId;
}
/**
 * Broadcasts the AuthKey room owner to all conference participants via MUC command.
 *
 * @param {Object} conference - lib-jitsi-meet conference instance.
 * @param {string} ownerId - Participant id of the room owner.
 * @returns {void}
 */
export function broadcastE2EERoomOwner(conference, ownerId) {
    if (!conference || !ownerId) {
        return;
    }
    conference.sendCommand(AUTHKEY_ROOM_OWNER_COMMAND, {
        attributes: {
            ownerId
        }
    });
}
/**
 * Applies a AuthKey room owner update received through a MUC command.
 *
 * @param {{ ownerId?: string }} attributes - Command attributes.
 * @param {string} senderId - Participant id that sent the command.
 * @param {IStore['dispatch']} dispatch - Redux dispatch.
 * @param {IStore['getState']} getState - Redux getState.
 * @returns {void}
 */
export function handleE2EERoomOwnerCommand(attributes, senderId, dispatch, getState) {
    if (!isExternallyManagedAuthKey(getState())) {
        return;
    }
    const ownerId = attributes?.ownerId;
    if (!ownerId || !senderId) {
        return;
    }
    const localParticipant = getLocalParticipant(getState());
    if (senderId === localParticipant?.id) {
        return;
    }
    const currentOwnerId = getE2EERoomOwnerId(getState());
    if (currentOwnerId === ownerId) {
        return;
    }
    if (!currentOwnerId) {
        if (ownerId !== senderId) {
            logger.warn('[authkey] ignored room owner command without prior owner', { senderId, ownerId });
            return;
        }
    }
    else if (senderId !== currentOwnerId) {
        const state = getState();
        const currentOwnerStillPresent = Boolean(currentOwnerId && getParticipantById(state, currentOwnerId));
        const localId = localParticipant?.id;
        const isMistakenSelfClaimCorrection = currentOwnerId === localId
            && senderId === ownerId
            && Boolean(getParticipantById(state, ownerId));
        if (currentOwnerStillPresent && !isMistakenSelfClaimCorrection) {
            logger.warn('[authkey] ignored room owner command from non-owner', { senderId, currentOwnerId });
            return;
        }
    }
    if (!getParticipantById(getState(), ownerId)) {
        logger.warn('[authkey] ignored room owner command for unknown participant', { ownerId, senderId });
        return;
    }
    dispatch(setE2EERoomOwner(ownerId, { broadcast: false }));
    logger.info('[authkey] applied room owner from command', { ownerId, senderId });
}
/**
 * Picks the earliest remote occupant id reported by lib-jitsi-meet (stable sort).
 *
 * @param {Object} conference - lib-jitsi-meet conference instance.
 * @returns {string|undefined}
 */
function pickFirstRemoteConferenceParticipantId(conference) {
    const remoteParticipants = conference?.getParticipants?.() ?? [];
    const ids = remoteParticipants
        .map((participant) => participant.getId())
        .filter(Boolean)
        .sort();
    return ids[0];
}
/**
 * Assigns the AuthKey E2EE room owner once per conference.
 * With muc_allowners every occupant receives moderator role from Prosody; the first
 * joiner (or the sole occupant already present) is treated as the E2EE owner.
 *
 * @param {IStore['dispatch']} dispatch - Redux dispatch.
 * @param {IStore['getState']} getState - Redux getState.
 * @param {Object} conference - lib-jitsi-meet conference instance.
 * @returns {void}
 */
export function assignE2EERoomOwnerIfNeeded(dispatch, getState, conference) {
    const state = getState();
    if (!isExternallyManagedAuthKey(state) || !conference) {
        return;
    }
    const localParticipant = getLocalParticipant(state);
    if (!localParticipant?.id) {
        return;
    }
    const remoteParticipants = conference?.getParticipants?.() ?? [];
    const currentOwnerId = getE2EERoomOwnerId(state);
    const firstRemoteId = pickFirstRemoteConferenceParticipantId(conference);
    // CONFERENCE_JOINED can run before lib-jitsi-meet lists remotes; undo a premature self-claim.
    if (currentOwnerId === localParticipant.id && firstRemoteId && firstRemoteId !== currentOwnerId) {
        dispatch(setE2EERoomOwner(firstRemoteId, { broadcast: false }));
        logger.info('[authkey] corrected local room owner to first remote occupant', {
            from: currentOwnerId,
            to: firstRemoteId
        });
        return;
    }
    if (currentOwnerId) {
        return;
    }
    if (firstRemoteId) {
        dispatch(setE2EERoomOwner(firstRemoteId));
        return;
    }
    // First joiner only: alone in the conference with no remote occupants yet.
    if (getParticipantCount(state) <= 1) {
        dispatch(setE2EERoomOwner(localParticipant.id));
    }
}
/**
 * Whether the local participant may enable AuthKey E2EE for this room.
 *
 * @param {IStateful} [stateful] - Redux state.
 * @returns {boolean}
 */
export function isAuthKeyE2EEModerator(stateful) {
    const state = stateful ? toState(stateful) : APP?.store?.getState();
    if (!state) {
        return false;
    }
    const localParticipant = getLocalParticipant(state);
    if (!localParticipant) {
        return false;
    }
    if (isExternallyManagedAuthKey(state)) {
        const ownerId = getE2EERoomOwnerId(state);
        if (ownerId) {
            return localParticipant.id === ownerId;
        }
        return getParticipantCount(state) <= 1;
    }
    return isParticipantModerator(localParticipant);
}
/**
 * Effective conference moderator for AuthKey rooms: the E2EE room owner when
 * externallyManagedKey is enabled, otherwise the XMPP moderator role.
 *
 * @param {IStateful} [stateful] - Redux state.
 * @returns {boolean}
 */
export function isEffectiveConferenceModerator(stateful) {
    return isAuthKeyE2EEModerator(stateful);
}
/**
 * Picks the next AuthKey room owner when the current owner leaves.
 *
 * @param {IStateful} stateful - Redux state.
 * @param {string} [excludeId] - Participant id to exclude.
 * @returns {string|undefined}
 */
export function pickNextE2EERoomOwnerId(stateful, excludeId) {
    const state = toState(stateful);
    const ids = [];
    const localParticipant = getLocalParticipant(state);
    if (localParticipant?.id && localParticipant.id !== excludeId) {
        ids.push(localParticipant.id);
    }
    for (const participant of getRemoteParticipants(state).values()) {
        if (participant.id
            && participant.id !== excludeId
            && !isScreenShareParticipant(participant)) {
            ids.push(participant.id);
        }
    }
    ids.sort();
    return ids[0];
}
/**
 * Transfers AuthKey room ownership when the current owner leaves the conference.
 *
 * @param {IStore['dispatch']} dispatch - Redux dispatch.
 * @param {IStore['getState']} getState - Redux getState.
 * @param {string} [leftParticipantId] - Participant that left.
 * @returns {void}
 */
export function transferE2EERoomOwnerOnLeave(dispatch, getState, leftParticipantId) {
    if (!leftParticipantId || !isExternallyManagedAuthKey(getState())) {
        return;
    }
    const ownerId = getE2EERoomOwnerId(getState());
    if (ownerId !== leftParticipantId) {
        return;
    }
    const nextOwnerId = pickNextE2EERoomOwnerId(getState(), leftParticipantId);
    dispatch(setE2EERoomOwner(nextOwnerId));
    logger.info('[authkey] transferred E2EE room owner', {
        from: leftParticipantId,
        to: nextOwnerId
    });
}
/**
 * Whether the local participant may kick the target participant.
 *
 * @param {IStateful} stateful - Redux state.
 * @param {string} targetParticipantId - Target participant id.
 * @returns {boolean}
 */
export function canKickParticipant(stateful, targetParticipantId) {
    if (!isLocalParticipantModerator(stateful)) {
        return false;
    }
    if (isExternallyManagedAuthKey(stateful)) {
        const ownerId = getE2EERoomOwnerId(stateful);
        if (ownerId && targetParticipantId === ownerId) {
            return false;
        }
    }
    return true;
}
/**
 *
 * @param {IStateful} stateful - Redux state.
 * @param {IParticipant} [participant] - Target participant.
 * @returns {boolean}
 */
export function shouldDisplayModeratorBadge(stateful, participant) {
    if (!participant) {
        return false;
    }
    if (!isExternallyManagedAuthKey(stateful)) {
        return isParticipantModerator(participant);
    }
    const ownerId = getE2EERoomOwnerId(stateful);
    if (ownerId) {
        return participant.id === ownerId;
    }
    return isParticipantModerator(participant) && getParticipantCount(toState(stateful)) <= 1;
}
/**
 * Whether the participant is the AuthKey authentication target for joiners.
 *
 * @param {IStateful} stateful - Redux state.
 * @param {IParticipant} [participant] - Target participant.
 * @returns {boolean}
 */
export function isAuthenticationRequestTarget(stateful, participant) {
    if (!participant?.id) {
        return false;
    }
    const moderatorId = findModeratorParticipantId(stateful);
    return Boolean(moderatorId && moderatorId === participant.id);
}
/**
 * Whether the room owner may toggle E2EE on right now.
 *
 * @param {IStateful} stateful - Redux state.
 * @param {boolean} enabling - True when turning E2EE on.
 * @returns {boolean}
 */
export function canToggleAuthKeyE2EE(stateful, enabling) {
    if (!isAuthKeyE2EEModerator(stateful)) {
        return false;
    }
    if (!enabling) {
        return true;
    }
    const state = toState(stateful);
    const { enabled: e2eeEnabled } = state['features/e2ee'];
    if (e2eeEnabled) {
        return true;
    }
    if (isExternallyManagedAuthKey(state)) {
        return true;
    }
    return getParticipantCount(state) < 2;
}
export function isCredentialFieldsComplete(email, credential, credentialPassword) {
    return Boolean(email
        && credential
        && credentialPassword);
}
export function isCredentialComplete() {
    if (typeof APP === 'undefined' || !APP.store) {
        return false;
    }
    const state = APP.store.getState();
    const localParticipant = getLocalParticipant(state);
    const settings = state['features/base/settings'];
    if (isCredentialFieldsComplete(localParticipant?.email, localParticipant?.credential, localParticipant?.credentialPassword)) {
        return true;
    }
    if (isCredentialFieldsComplete(settings?.email, settings?.credential, settings?.credentialPassword)) {
        return true;
    }
    const wasmCredentials = getSafeLocalCredentialInfo();
    return isCredentialFieldsComplete(wasmCredentials.email, wasmCredentials.credential, wasmCredentials.credentialPassword);
}
/**
 * Reads AuthKey credential info from the service instance when available.
 * Never throws — returns empty strings if the authenticator is not ready yet.
 *
 * @returns {{ email: string, credential: string, credentialPassword: string }}
 */
export function getSafeLocalCredentialInfo() {
    const authenticator = getAuthKeyAuthenticator();
    if (!authenticator) {
        return {
            credential: '',
            credentialPassword: '',
            email: ''
        };
    }
    return authenticator.getLocalCredentialInfo();
}
/**
 * Returns the joined conference instance, if any.
 *
 * @param {IStateful} stateful - The redux store, state, or getState function.
 * @returns {Object|undefined}
 */
export function getJoinedConference(stateful) {
    return getConferenceState(toState(stateful)).conference;
}
function isE2EEEnabledValue(value) {
    return value === true || value === 'true';
}
/**
 * Returns whether the current room is already running with E2EE enabled.
 * For joiners this can be true before the local conference has enabled E2EE,
 * because existing participants advertise the state through presence.
 *
 * @param {IStateful} stateful - Redux state.
 * @returns {boolean}
 */
export function isAuthKeyRoomE2EEEnabled(stateful) {
    const state = toState(stateful);
    const reduxParticipantEnabled = Array.from(getRemoteParticipants(state).values())
        .some((participant) => Boolean(participant.e2eeEnabled));
    if (reduxParticipantEnabled) {
        return true;
    }
    const conference = getJoinedConference(state);
    return (conference?.getParticipants?.() ?? [])
        .some((participant) => isE2EEEnabledValue(participant.getProperty?.('e2ee.enabled')));
}
/**
 * Applies the AuthKey session key directly on the joined conference.
 *
 * @param {IStore['getState']} getState - Redux getState.
 * @returns {Promise<void>}
 */
export async function applyAuthKeyEncryptionKey(getState) {
    const authenticator = getAuthKeyAuthenticator();
    if (!authenticator) {
        logger.warn('[authkey] applyAuthKeyEncryptionKey: authKeyAuthenticator is null');
        return;
    }
    const joinedConference = getJoinedConference(getState);
    if (!joinedConference?.isE2EESupported()) {
        logger.debug('[authkey] applyAuthKeyEncryptionKey: E2EE not supported, skip');
        return;
    }
    authenticator.updateSessionKey();
    const sessionKey = authenticator.getSessionKeyBytes();
    const encryptionKey = await window.crypto.subtle.importKey('raw', new Uint8Array(sessionKey), 'AES-GCM', false, ['encrypt', 'decrypt']);
    joinedConference.setMediaEncryptionKey({
        encryptionKey,
        index: 1
    });
}
export function setupAuthKey(dispatch, getState) {
    if (!getState || !getJoinedConference(getState())) {
        logger.debug('[authkey] setupAuthKey: conference not joined yet, skip setMediaEncryptionKey');
        return Promise.resolve();
    }
    return applyAuthKeyEncryptionKey(getState)
        .catch((error) => logger.error('[authkey] setupAuthKey failed', error));
}
export function requestModeratorForAuthentication(dispatch, moderatorId) {
    if (!isCredentialComplete()) {
        logger.warn('[authkey] credential information is not complete.');
        dispatch(showWarningNotification({
            titleKey: 'notify.e2ee',
            descriptionKey: 'notify.e2eeCredentialIncomplete',
            customActionNameKey: ['e2ee.completeCredential'],
            customActionType: [BUTTON_TYPES.PRIMARY],
            customActionHandler: [() => batch(() => dispatch(openSettingsDialog(SETTINGS_TABS.PROFILE)))],
            icon: NOTIFICATION_ICON.WARNING
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        return;
    }
    const authenticator = getAuthKeyAuthenticator();
    if (!authenticator) {
        logger.warn('[authkey] authKey authenticator is not loaded yet.');
        dispatch(showErrorNotification({
            titleKey: 'notify.e2ee',
            descriptionKey: 'notify.e2eeAuthKeyNotLoaded',
            icon: NOTIFICATION_ICON.ERROR
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        return;
    }
    const requestMessage = {
        type: AUTHKEY_MESSAGE_TYPE.AKE_REQUEST,
        payload: 'ake request'
    };
    const participantName = getParticipantDisplayName(APP.store.getState(), moderatorId);
    const localConference = getCurrentConference(APP.store.getState());
    localConference?.sendMessage(requestMessage, moderatorId);
    dispatch(showNotification({
        titleKey: 'notify.e2ee',
        descriptionKey: 'e2ee.akeRequest',
        descriptionArguments: { name: participantName }
    }, NOTIFICATION_TIMEOUT_TYPE.SHORT));
}
export async function startAKEwithParticipant(dispatch, participantId) {
    if (!isCredentialComplete()) {
        logger.warn('[authkey] credential information is not complete.');
        dispatch(showWarningNotification({
            titleKey: 'notify.e2ee',
            descriptionKey: 'notify.e2eeCredentialIncomplete',
            customActionNameKey: ['e2ee.completeCredential'],
            customActionType: [BUTTON_TYPES.PRIMARY],
            customActionHandler: [() => batch(() => dispatch(openSettingsDialog(SETTINGS_TABS.PROFILE)))],
            icon: NOTIFICATION_ICON.WARNING
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        return;
    }
    const authenticator = await ensureAuthKeyServiceReady();
    if (!authenticator) {
        logger.warn('[authkey] AuthKey service is not ready.');
        showAuthKeyServiceNotReadyNotification(dispatch);
        return;
    }
    const localCredential = getSafeLocalCredentialInfo();
    const akeState = await authenticator.serviceWASM.AkeStart(localCredential.email, participantId, localCredential.credential, localCredential.credentialPassword);
    if (!akeState.success) {
        logger.error('[authkey] Generate AKE init state failed: ', akeState);
        dispatch(showErrorNotification({
            titleKey: 'notify.e2ee',
            descriptionKey: 'notify.e2eeAuthError',
            icon: NOTIFICATION_ICON.ERROR
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        dispatch(openSettingsDialog(SETTINGS_TABS.PROFILE));
        return;
    }
    authenticator.setAKESessionState(participantId, akeState.data);
    const akeInitMessage = {
        type: AUTHKEY_MESSAGE_TYPE.AKE_INIT,
        payload: {
            leaderId: akeState.data.leader_id,
            leaderEphemeralPublicKey: akeState.data.ecdh_pk,
            leaderEphemeralKemPublicKey: akeState.data.pqkem_pk,
            transformedToken: akeState.data.transformed_token
        }
    };
    const participantName = getParticipantDisplayName(APP.store.getState(), participantId);
    const localConference = getCurrentConference(APP.store.getState());
    localConference?.sendMessage(akeInitMessage, participantId);
    dispatch(showNotification({
        titleKey: 'notify.e2ee',
        descriptionKey: 'e2ee.akeInitSent',
        descriptionArguments: { name: participantName }
    }, NOTIFICATION_TIMEOUT_TYPE.SHORT));
    authenticator.setLocalAuthenticationStatus(true);
}
export async function responseAKEwithModerator(dispatch, moderatorId, payload) {
    if (!isCredentialComplete()) {
        logger.warn('[authkey] credential information is not complete.');
        dispatch(showWarningNotification({
            titleKey: 'notify.e2ee',
            descriptionKey: 'notify.e2eeCredentialIncomplete',
            customActionNameKey: ['e2ee.completeCredential'],
            customActionType: [BUTTON_TYPES.PRIMARY],
            customActionHandler: [() => batch(() => dispatch(openSettingsDialog(SETTINGS_TABS.PROFILE)))],
            icon: NOTIFICATION_ICON.WARNING
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        return;
    }
    const authenticator = await ensureAuthKeyServiceReady();
    if (!authenticator) {
        logger.warn('[authkey] AuthKey service is not ready.');
        showAuthKeyServiceNotReadyNotification(dispatch);
        return;
    }
    const localCredential = getSafeLocalCredentialInfo();
    const akeResponseState = await authenticator.serviceWASM.AkeResponse(payload.leaderId, localCredential.email, localCredential.credential, localCredential.credentialPassword, payload.leaderEphemeralPublicKey, payload.leaderEphemeralKemPublicKey, payload.transformedToken);
    if (!akeResponseState.success) {
        logger.error('[authkey] Generate AKE response state failed: ', akeResponseState);
        dispatch(showErrorNotification({
            titleKey: 'notify.e2ee',
            descriptionKey: 'notify.e2eeAuthError',
            icon: NOTIFICATION_ICON.ERROR
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        dispatch(openSettingsDialog(SETTINGS_TABS.PROFILE));
        return;
    }
    authenticator.setAKESessionState(moderatorId, akeResponseState.data);
    const akeResponseMessage = {
        type: AUTHKEY_MESSAGE_TYPE.AKE_RESPONSE,
        payload: {
            leaderId: akeResponseState.data.leader_id,
            userId: akeResponseState.data.user_id,
            userEphemeralPublicKey: akeResponseState.data.ecdh_pk,
            userEphemeralKemPublicKey: akeResponseState.data.pqkem_pk,
            userKemCiphertext: akeResponseState.data.pqkem_ct,
            transformedToken: akeResponseState.data.transformed_token
        }
    };
    const localConference = getCurrentConference(APP.store.getState());
    localConference?.sendMessage(akeResponseMessage, moderatorId);
}
export async function finishAKEwithParticipant(dispatch, participantId, payload) {
    const authenticator = await ensureAuthKeyServiceReady();
    if (!authenticator) {
        logger.warn('[authkey] finishAKEwithParticipant: AuthKey service not ready');
        showAuthKeyServiceNotReadyNotification(dispatch);
        return;
    }
    const previousState = authenticator.getAKESessionState(participantId);
    if (!previousState) {
        dispatch(showErrorNotification({
            titleKey: 'notify.e2ee',
            descriptionKey: 'notify.e2eeAuthError'
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        return;
    }
    previousState.user_id = payload.userId;
    const akeFinishState = await authenticator.serviceWASM.AkeFinal(payload.userId, payload.userEphemeralPublicKey, payload.userEphemeralKemPublicKey, payload.userKemCiphertext, payload.transformedToken, previousState);
    if (!akeFinishState.success) {
        logger.error('[authkey] Failed to auth the user: ', akeFinishState);
        return;
    }
    const akeFinishMessage = {
        type: AUTHKEY_MESSAGE_TYPE.AKE_FINAL,
        payload: {
            leaderId: previousState.leader_id,
            userId: previousState.user_id,
            encryptedSessionKey: akeFinishState.data
        }
    };
    const localConference = getCurrentConference(APP.store.getState());
    localConference?.sendMessage(akeFinishMessage, participantId);
    authenticator.setParticipantAuthenticationStatus(participantId, true);
    dispatch(hideNotification(`${participantId}-ake-request`));
}
export async function extractAKEsessionKey(dispatch, participantId, payload) {
    const authenticator = await ensureAuthKeyServiceReady();
    if (!authenticator) {
        logger.warn('[authkey] extractAKEsessionKey: AuthKey service not ready');
        showAuthKeyServiceNotReadyNotification(dispatch);
        return;
    }
    const previousState = authenticator.getAKESessionState(participantId);
    if (!previousState) {
        dispatch(showErrorNotification({
            titleKey: 'notify.e2ee',
            descriptionKey: 'notify.e2eeAuthError'
        }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
        return;
    }
    const akeSessionKey = await authenticator.serviceWASM.AkeEnd(payload.encryptedSessionKey.key_ct, payload.encryptedSessionKey.kem_ct, previousState);
    authenticator.setSessionKey(akeSessionKey.data);
    const sessionKeyBytes = authenticator.getSessionKeyBytes();
    dispatch(setMediaEncryptionKey({
        exportedKey: sessionKeyBytes,
        index: 1
    }));
    authenticator.setLocalAuthenticationStatus(true);
    authenticator.setParticipantAuthenticationStatus(participantId, true);
    dispatch(showNotification({
        titleKey: 'notify.e2ee',
        descriptionKey: 'notify.e2eeAuthSuccess',
        icon: NOTIFICATION_ICON.SUCCESS
    }, NOTIFICATION_TIMEOUT_TYPE.MEDIUM));
}
export function getParticipantVerificationStatus(participantId) {
    const authenticator = getAuthKeyAuthenticator();
    if (!authenticator) {
        return false;
    }
    return Boolean(authenticator.getParticipantAuthenticationStatus(participantId));
}
export function getLocalAuthenticationStatus() {
    const authenticator = getAuthKeyAuthenticator();
    if (!authenticator) {
        return false;
    }
    return Boolean(authenticator.getLocalAuthenticationStatus());
}
/**
 * Returns the id of a conference moderator, if any.
 *
 * @param {IStateful} stateful - Redux state.
 * @returns {string|undefined}
 */
export function findModeratorParticipantId(stateful) {
    const state = toState(stateful);
    if (isExternallyManagedAuthKey(state)) {
        const ownerId = getE2EERoomOwnerId(state);
        if (ownerId) {
            return ownerId;
        }
        const firstRemoteId = pickFirstRemoteConferenceParticipantId(getJoinedConference(state));
        if (firstRemoteId && isAuthKeyRoomE2EEEnabled(state)) {
            return firstRemoteId;
        }
    }
    const localParticipant = getLocalParticipant(state);
    if (isAuthKeyE2EEModerator(state)) {
        return localParticipant?.id;
    }
    const conference = getJoinedConference(state);
    if (conference) {
        for (const participant of conference.getParticipants()) {
            if (shouldDisplayModeratorBadge(state, {
                id: participant.getId(),
                role: participant.getRole?.()
            })) {
                return participant.getId();
            }
        }
    }
    for (const participant of getRemoteParticipants(state).values()) {
        if (shouldDisplayModeratorBadge(state, participant)) {
            return participant.id;
        }
    }
    return undefined;
}
/**
 * When E2EE is already enabled, a joiner with AuthKey credentials must complete AKE
 * with the moderator to receive the shared session key.
 *
 * @param {IStore['dispatch']} dispatch - Redux dispatch.
 * @param {IStore['getState']} getState - Redux getState.
 * @param {IParticipant} participant - The remote participant that joined.
 * @returns {void}
 */
export async function handleAuthKeyOnRemoteParticipantJoined(dispatch, getState, participant) {
    const state = getState();
    const { e2ee = {} } = state['features/base/config'];
    const conference = getJoinedConference(state);
    if (!e2ee.externallyManagedKey || !conference?.isE2EEEnabled?.()) {
        return;
    }
    if (!participant?.id || participant.local || isScreenShareParticipant(participant)) {
        return;
    }
    if (!isCredentialComplete()) {
        return;
    }
    const authenticator = await ensureAuthKeyServiceReady();
    if (!authenticator) {
        return;
    }
    if (isAuthKeyE2EEModerator(state)) {
        if (getParticipantVerificationStatus(participant.id)) {
            return;
        }
        window.setTimeout(() => {
            if (!getParticipantVerificationStatus(participant.id)) {
                void startAKEwithParticipant(dispatch, participant.id);
            }
        }, 1000);
        return;
    }
    if (getLocalAuthenticationStatus()) {
        return;
    }
    const moderatorId = findModeratorParticipantId(state);
    if (moderatorId) {
        window.setTimeout(() => {
            if (!getLocalAuthenticationStatus()) {
                requestModeratorForAuthentication(dispatch, moderatorId);
            }
        }, 1000);
    }
}
/**
 * Opens the profile settings for an unauthenticated joiner entering an encrypted conference.
 *
 * @param {IStore['dispatch']} dispatch - Redux dispatch.
 * @param {IStore['getState']} getState - Redux getState.
 * @returns {void}
 */
export function showAuthKeyCredentialPromptOnConferenceJoined(dispatch, getState) {
    const state = getState();
    const { e2ee = {} } = state['features/base/config'];
    const conference = getJoinedConference(state);
    if (!e2ee.externallyManagedKey || !conference || !isAuthKeyRoomE2EEEnabled(state) || isAuthKeyE2EEModerator(state)) {
        return;
    }
    if (!getLocalAuthenticationStatus() && authKeyProfileSettingsPromptedConference !== conference) {
        authKeyProfileSettingsPromptedConference = conference;
        dispatch(openSettingsDialog(SETTINGS_TABS.PROFILE));
    }
}
/**
 * Joiners entering a conference that already has E2EE enabled must authenticate with the moderator.
 *
 * @param {IStore['dispatch']} dispatch - Redux dispatch.
 * @param {IStore['getState']} getState - Redux getState.
 * @returns {void}
 */
export function handleAuthKeyOnConferenceJoined(dispatch, getState) {
    const state = getState();
    const { e2ee = {} } = state['features/base/config'];
    const conference = getJoinedConference(state);
    if (!e2ee.externallyManagedKey || !conference || !isAuthKeyRoomE2EEEnabled(state) || isAuthKeyE2EEModerator(state)) {
        return;
    }
    showAuthKeyCredentialPromptOnConferenceJoined(dispatch, getState);
    if (!isCredentialComplete() || getLocalAuthenticationStatus()) {
        return;
    }
    const moderatorId = findModeratorParticipantId(state);
    if (!moderatorId) {
        return;
    }
    window.setTimeout(() => {
        if (!getLocalAuthenticationStatus()) {
            requestModeratorForAuthentication(dispatch, moderatorId);
        }
    }, 1500);
}
